import React, { createContext, useContext, useState, useCallback } from 'react';
import type { GameState, CombatState } from '@/lib/types/game';

interface GameContextType {
  gameState: GameState | null;
  combatState: CombatState | null;
  isLoading: boolean;
  error: string | null;
  setGameState: (state: GameState | null) => void;
  setCombatState: (state: CombatState | null) => void;
  setIsLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  refreshGameState: () => Promise<void>;
}

const GameContext = createContext<GameContextType | undefined>(undefined);

export const useGameContext = () => {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGameContext must be used within GameProvider');
  }
  return context;
};

interface GameProviderProps {
  children: React.ReactNode;
}

export const GameProvider: React.FC<GameProviderProps> = ({ children }) => {
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [combatState, setCombatState] = useState<CombatState | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Refresh game state from contract
  const refreshGameState = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // This will be implemented by the useContract hook
      // For now, this is a placeholder
      console.log('Refreshing game state...');

      // TODO: Call contract to get latest game state
      // const newState = await gameContract.getGameState(address);
      // setGameState(newState);

      // TODO: If in combat, get combat state
      // const newCombatState = await combatContract.getCombatState(address);
      // setCombatState(newCombatState);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to refresh game state';
      setError(errorMessage);
      console.error('Game state refresh error:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const value: GameContextType = {
    gameState,
    combatState,
    isLoading,
    error,
    setGameState,
    setCombatState,
    setIsLoading,
    setError,
    refreshGameState,
  };

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
};
